'use client';

import React from 'react';

import { Check } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';

import { COPY } from '@/shared/constants/copies';
import { ExtraCreditsTier, ISExtraCredits } from '@/shared/services/payment/payment.types';

import { cn } from '@/lib/utils';

type ExtraCreditsPickerProps = {
	extraCredits: ISExtraCredits[];
	selectedTier: ExtraCreditsTier | null;
	onSelect: (tier: ExtraCreditsTier | null) => void;
	currentTier?: ExtraCreditsTier | null;
	/** Offers the plan's included limit as the first option. */
	allowNone?: boolean;
	/** The plan's own monthly credits, used to show totals rather than top-up amounts. */
	planCredits?: number;
	currency?: string;
	isLoading?: boolean;
	disabled?: boolean;
	className?: string;
};

function OptionRow({
	title,
	badge,
	isSelected,
	disabled,
	onClick,
}: {
	title: string;
	badge?: string;
	isSelected: boolean;
	disabled?: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			aria-pressed={isSelected}
			className={cn(
				'flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors',
				isSelected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
				disabled && 'cursor-not-allowed opacity-50'
			)}
		>
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium text-primary">{title}</span>
				{badge ? (
					<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-secondary">
						{badge}
					</span>
				) : null}
			</div>

			{isSelected ? <Check className="size-4 shrink-0 text-primary" /> : null}
		</button>
	);
}

/** Matches the three recommended tiers, so the list does not jump on load. */
const SKELETON_ROW_COUNT = 3;

/**
 * Selectable list of extra-credits tiers.
 *
 * Presentational and controlled — the catalog, the selection and the mutation all
 * belong to the caller, so Billing and the pre-checkout upsell share this component.
 * Tiers render in API order and are never hardcoded. The catalog carries every tier
 * the system sells; `recommended_tiers` narrows it to the three that suit the target
 * plan, so the list stays scannable. If the API recommends nothing, show the lot
 * rather than an empty picker.
 */
export const ExtraCreditsPicker = React.memo(function ExtraCreditsPicker({
	extraCredits,
	selectedTier,
	onSelect,
	currentTier,
	allowNone,
	planCredits = 0,
	currency,
	isLoading,
	disabled,
	className,
}: ExtraCreditsPickerProps) {
	// Fall back to the full catalog when nothing is recommended, so a missing or
	// unrecognised `recommended_tiers` can never leave the user with no choices.
	const visibleOptions = React.useMemo(() => {
		const recommended = extraCredits.filter((option) => option.recommended);
		return recommended.length > 0 ? recommended : extraCredits;
	}, [extraCredits]);

	if (isLoading) {
		return (
			<div className={cn('flex flex-col gap-2', className)}>
				{Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
					<Skeleton
						key={index}
						className="h-[50px] w-full rounded-lg"
					/>
				))}
			</div>
		);
	}

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{allowNone && (
				<OptionRow
					title={COPY.billing.extraCreditsIncludedOption(planCredits)}
					badge={COPY.billing.extraCreditsIncluded}
					isSelected={selectedTier === null}
					disabled={disabled}
					onClick={() => onSelect(null)}
				/>
			)}
			{visibleOptions.map((extraCredits) => {
				// The tier already owned is not a choice — picking it would submit a
				// change that changes nothing.
				const isCurrent = extraCredits.extraCreditsTier === currentTier;

				return (
					<OptionRow
						key={extraCredits.extraCreditsTier}
						title={COPY.billing.extraCreditsOption(planCredits + extraCredits.monthlyCredits)}
						badge={COPY.billing.extraCreditsPriceTag(extraCredits.extraCreditsTier, currency)}
						isSelected={selectedTier === extraCredits.extraCreditsTier}
						disabled={disabled || isCurrent}
						onClick={() => onSelect(extraCredits.extraCreditsTier)}
					/>
				);
			})}
		</div>
	);
});
